import { cn } from '@/lib/utils/cn';
import { dateValue, formatDate, formatDayMonth } from '@/lib/utils/format';
import { ChartTooltip, useChartTooltip } from './chart-tooltip';
import { SrTable, cssVars, f1 } from './shared';
import { MILESTONE_STATUS_LABEL, type MilestoneStatus } from './milestone-gantt';
import { useEntryMotion } from './use-entry-motion';

/**
 * GoLiveRunway — kickoff to go-live on one line (prototype `runway()`):
 * the elapsed part drawn dark up to an amber "Today" tick, each milestone
 * a dot at its projected date coloured by status, and the go-live as a
 * diamond at the end. The countdown card's figure.
 */
export interface RunwayMilestone {
  id: string;
  name: string;
  /** Projected (or done) date, ISO. */
  date: string;
  status: MilestoneStatus;
}

export interface GoLiveRunwayProps {
  /** Kickoff, ISO (projects.start_date). */
  start: string;
  /** Planned go-live, ISO. */
  goLive: string;
  today: string;
  /** Leave the go-live milestone itself out; the diamond draws it. */
  milestones: RunwayMilestone[];
  motionKey?: string | number;
  className?: string;
}

const W = 320;
const Y = 26;
const X0 = 6;
const X1 = W - 10;

const DOT: Record<MilestoneStatus, string> = {
  done: 'var(--ink-500)',
  late: 'var(--posture-critical)',
  'at-risk': 'var(--posture-at-risk)',
  compressed: 'var(--posture-at-risk)',
  'on-track': 'var(--posture-healthy)',
};

export function GoLiveRunway({
  start,
  goLive,
  today,
  milestones,
  motionKey,
  className,
}: GoLiveRunwayProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const { tip, bind } = useChartTooltip();
  const a = dateValue(start);
  const b = dateValue(goLive);
  const clamp = (v: number) => Math.min(X1, Math.max(X0, v));
  const x = (ms: number) => clamp(b === a ? X0 : X0 + ((ms - a) / (b - a)) * (X1 - X0));
  const tx = x(dateValue(today));

  return (
    <div key={runKey} className={cn(animate && 'chart-anim', className)}>
      <svg
        viewBox={`0 0 ${W} 52`}
        role="img"
        aria-label={`Timeline from kickoff ${formatDate(start)} to go-live ${formatDate(goLive)}`}
        className="chart-svg chart-svg-sm block h-auto w-full overflow-visible"
      >
        <line
          x1={X0}
          x2={X1}
          y1={Y}
          y2={Y}
          stroke="var(--ink-100)"
          strokeWidth={6}
          strokeLinecap="round"
        />
        <line
          className="a-draw"
          pathLength={1}
          x1={X0}
          x2={f1(tx)}
          y1={Y}
          y2={Y}
          stroke="var(--ink-700)"
          strokeWidth={6}
          strokeLinecap="round"
        />
        {milestones.map((m, i) => (
          <circle
            key={m.id}
            className="a-fade"
            style={cssVars({ '--i': i })}
            cx={f1(x(dateValue(m.date)))}
            cy={Y}
            r={5}
            fill={DOT[m.status]}
            stroke="var(--paper-0)"
            strokeWidth={2}
            {...bind({
              title: m.name,
              value: `${m.status === 'done' ? 'Done' : 'Projected'} ${formatDayMonth(m.date)}`,
              sub: MILESTONE_STATUS_LABEL[m.status],
            })}
          />
        ))}
        <path
          d={`M${X1} ${Y - 9} l7 9 l-7 9 l-7 -9 Z`}
          fill="var(--ink-900)"
          stroke="var(--paper-0)"
          strokeWidth={2}
          {...bind({ title: 'Go-live', value: formatDate(goLive), sub: 'planned' })}
        />
        <line
          x1={f1(tx)}
          x2={f1(tx)}
          y1={Y - 14}
          y2={Y + 8}
          stroke="var(--accent-amber-600)"
          strokeWidth={2}
        />
        <text
          x={f1(tx)}
          y={Y - 18}
          textAnchor="middle"
          className="fill-accent-amber-700 font-semibold"
        >
          Today
        </text>
        <text x={X0} y={Y + 22}>
          Kickoff {formatDayMonth(start)}
        </text>
        <text x={X1} y={Y + 22} textAnchor="end">
          Go-live {formatDayMonth(goLive)}
        </text>
      </svg>
      <SrTable
        caption="Kickoff to go-live"
        head={['Point', 'Date', 'Status']}
        rows={[
          ['Kickoff', formatDate(start), ''],
          ...milestones.map((m) => [m.name, formatDate(m.date), MILESTONE_STATUS_LABEL[m.status]]),
          ['Today', formatDate(today), ''],
          ['Go-live', formatDate(goLive), 'planned'],
        ]}
      />
      <ChartTooltip tip={tip} />
    </div>
  );
}
